import { useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Typography, Divider,
  ToggleButton, ToggleButtonGroup,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  tenantsApi, type TenantSettingsDto, type ServiceAccountMappingMode,
} from "@/api/endpoints";
import { PageHeader } from "@/components/PageHeader";
import { errorMessage } from "@/api/client";
import { formatDate } from "@/shared/datetime";
import { useAuth } from "@/store/auth";

/**
 * Tenant settings. Mostly read-only: the headline value is the tax regime, shown alongside
 * identity and a clear note that switching regimes post-go-live is a Finance-ticketed migration.
 *
 * The one setting that IS changeable here is the service account-head mapping — already-posted
 * journal entries keep the accounts they were posted to, so only future postings follow a change.
 *
 * Path: /admin/tenant-settings (registered in App.tsx).
 */
export function TenantSettingsPage() {
  const q = useQuery({
    queryKey: ["tenant", "current"],
    queryFn: tenantsApi.current,
    staleTime: 5 * 60_000,
  });

  if (q.isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (q.error || !q.data) {
    return <Alert severity="error" sx={{ m: 2 }}>{errorMessage(q.error)}</Alert>;
  }

  const t = q.data;
  return (
    <Box sx={{ p: 3, maxWidth: 720 }}>
      <PageHeader breadcrumbs={["Administration"]} title="Tenant settings" />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Tenant identity and regime configuration. Operational data (branches,
        warehouses, users, feature flags) is managed in the dedicated pages.
      </Typography>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="overline" color="text.secondary">Identity</Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={4} sx={{ mt: 1 }}>
          <Field label="Name" value={t.name} />
          <Field label="Code" value={t.code} />
          <Field label="Status">
            <Chip size="small"
              color={t.isActive ? "success" : "default"}
              label={t.isActive ? "Active" : "Inactive"} />
          </Field>
          <Field label="Created" value={formatDate(t.createdAt)} />
        </Stack>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="overline" color="text.secondary">Tax regime</Typography>
          <LockIcon fontSize="small" sx={{ color: "text.disabled" }} />
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={4} alignItems="flex-start">
          <Stack>
            <RegimeChip regime={t.taxRegime} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              {regimeDescription(t.taxRegime)}
            </Typography>
          </Stack>
        </Stack>

        <Divider sx={{ my: 2 }} />
        <Alert severity="info" variant="outlined">
          <Typography variant="body2">
            <strong>Changing the tax regime is not a UI action.</strong> Switching after
            go-live requires migrating historical journal entries, tax-account balances,
            and customer/vendor tax identifiers. Open a Finance ticket and the migration
            runs out of band.
          </Typography>
        </Alert>
      </Paper>

      <ServiceAccountMappingCard tenant={t} />
    </Box>
  );
}

/**
 * The account-head mapping switch. Unlike the tax regime this one is safe to change after
 * go-live, so it is an actual control — permission-gated on accounting.account.manage and
 * audited server-side. The API refuses the switch to group mapping while any group holding
 * service items has no account head; that error surfaces here verbatim.
 */
function ServiceAccountMappingCard({ tenant }: { tenant: TenantSettingsDto }) {
  const qc = useQueryClient();
  const canManage = useAuth(s => s.hasPermission("accounting.account.manage"));
  const [pending, setPending] = useState<ServiceAccountMappingMode | null>(null);

  const mutation = useMutation({
    meta: { successMessage: "Service account mapping updated" },
    mutationFn: (mode: ServiceAccountMappingMode) => tenantsApi.setServiceAccountMapping(mode),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenant", "current"] });
      qc.invalidateQueries({ queryKey: ["services"] });
      setPending(null);
    },
  });

  const current = tenant.serviceAccountMappingMode;
  const selected = pending ?? current;
  const dirty = selected !== current;

  return (
    <Paper sx={{ p: 3, mt: 2 }}>
      <Typography variant="overline" color="text.secondary">Service account mapping</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
        Where a service line reads its revenue account head from. Some clients maintain the chart
        of accounts on each service item; others map it once per service group.
      </Typography>

      {mutation.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>
      )}

      <ToggleButtonGroup
        exclusive
        value={selected}
        onChange={(_, v: ServiceAccountMappingMode | null) => v && setPending(v)}
        disabled={!canManage || mutation.isPending}
        sx={{ mb: 2, flexWrap: "wrap" }}
      >
        <ToggleButton value="ServiceItem" sx={{ textTransform: "none", px: 2 }}>
          <Stack alignItems="flex-start">
            <Typography variant="body2" fontWeight={600}>Service item</Typography>
            <Typography variant="caption" color="text.secondary">Head mapped on each service</Typography>
          </Stack>
        </ToggleButton>
        <ToggleButton value="ServiceGroup" sx={{ textTransform: "none", px: 2 }}>
          <Stack alignItems="flex-start">
            <Typography variant="body2" fontWeight={600}>Service group</Typography>
            <Typography variant="caption" color="text.secondary">Head mapped once per group</Typography>
          </Stack>
        </ToggleButton>
      </ToggleButtonGroup>

      {dirty && (
        <Alert
          severity="warning" sx={{ mb: 2 }}
          action={
            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={() => setPending(null)}>Cancel</Button>
              <Button size="small" variant="contained" disabled={mutation.isPending}
                onClick={() => mutation.mutate(selected)}>
                {mutation.isPending ? <CircularProgress size={16} color="inherit" /> : "Apply"}
              </Button>
            </Stack>
          }
        >
          {selected === "ServiceGroup"
            ? "Every service group holding service items will need an account head. The change is rejected if any is unmapped."
            : "Service items will post to their own account head. Groups with services but no head are no longer a problem."}
        </Alert>
      )}

      {!canManage && (
        <Typography variant="caption" color="text.secondary">
          Requires the accounting.account.manage permission to change.
        </Typography>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
        Already-posted journal entries keep the accounts they were posted to — only future service
        postings follow the new mode. A service with nothing mapped on either side falls back to
        the default 4300 “Service Revenue” head.
      </Typography>
    </Paper>
  );
}

function Field({ label, value, children }: { label: string; value?: string; children?: React.ReactNode }) {
  return (
    <Stack>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      {value !== undefined ? <Typography variant="body1">{value}</Typography> : children}
    </Stack>
  );
}

function RegimeChip({ regime }: { regime: TenantSettingsDto["taxRegime"] }) {
  const map = {
    Gst:  { label: "India GST",      color: "primary"   as const },
    Vat:  { label: "VAT",            color: "secondary" as const },
    None: { label: "No statutory tax", color: "default" as const },
  };
  const m = map[regime];
  return <Chip color={m.color} label={m.label} sx={{ fontWeight: 600 }} />;
}

function regimeDescription(regime: TenantSettingsDto["taxRegime"]): string {
  switch (regime) {
    case "Gst":
      return "CGST/SGST intra-state, IGST inter-state. GL accounts 1410 (input) / 2200 (output). Eligible for IRP e-invoice and e-way bill integrations.";
    case "Vat":
      return "Single-component VAT. GL accounts 1411 (input) / 2210 (output). Jurisdiction-specific filing wired per customer (UAE FTA, KSA ZATCA, UK HMRC, EU OSS).";
    case "None":
      return "Template tenant or jurisdiction without statutory tax. Invoices post sub-total and AR/AP only; no tax line is generated.";
  }
}
